/**
 * Custom queues beside the queue library (RESTORE_PLAN R1.6, from the pre-November
 * QueueManagement): free-typed queues with a name, a description and, optionally, their JQL,
 * saved on the engagement config next to the library's switches. The server checks them again
 * (supabase/functions/server/queueInboxCore.ts `normalizeQueueConfig`).
 */
import type { QueueSelection } from '../types/queue';

export interface CustomQueue {
  id: string;
  name: string;
  description?: string;
  jql?: string;
  createdAt: string;
  createdBy?: string;
}

export interface QueueConfig {
  selections: QueueSelection[];
  custom: CustomQueue[];
}

export interface CustomQueueInput {
  name: string;
  description?: string;
  jql?: string;
}

/** The engagement config's queues in either shape (older configs have no custom list). */
export function queueConfigOf(engagementConfig: { queues?: Partial<QueueConfig> | null } | null | undefined): QueueConfig {
  const q = engagementConfig?.queues || {};
  return {
    selections: Array.isArray(q.selections) ? q.selections : [],
    custom: Array.isArray(q.custom) ? q.custom : [],
  };
}

/** The config to save: everything else on the engagement config kept, the queues replaced as a whole. */
export function withQueueConfig<T extends Record<string, unknown>>(engagementConfig: T, queues: QueueConfig): T & { queues: QueueConfig } {
  return { ...engagementConfig, queues: { ...(engagementConfig.queues as object || {}), ...queues } };
}

/**
 * Why a custom queue cannot be saved, or null. Its name must be new among the custom queues and
 * the library's names (a queue switched on from the library already has that name).
 */
export function customQueueProblem(input: CustomQueueInput, custom: CustomQueue[], libraryNames: string[], editingId?: string): string | null {
  const name = input.name.trim();
  if (!name) return 'Queue name is required';
  if (name.length > 80) return 'Queue names are 80 characters or fewer';
  const n = name.toLowerCase();
  if (custom.some((q) => q.id !== editingId && q.name.trim().toLowerCase() === n)) return 'A custom queue with this name already exists';
  if (libraryNames.some((l) => l.trim().toLowerCase() === n)) return 'The queue library already has a queue with this name; switch it on there instead';
  return null;
}

export function addCustomQueue(custom: CustomQueue[], input: CustomQueueInput, createdBy: string | undefined, now = new Date()): CustomQueue[] {
  const description = input.description?.trim();
  const jql = input.jql?.trim();
  return [
    ...custom,
    {
      id: `queue_custom_${now.getTime()}_${Math.random().toString(36).slice(2, 7)}`,
      name: input.name.trim(),
      ...(description ? { description } : {}),
      ...(jql ? { jql } : {}),
      createdAt: now.toISOString(),
      ...(createdBy ? { createdBy } : {}),
    },
  ];
}

export function updateCustomQueue(custom: CustomQueue[], id: string, input: CustomQueueInput): CustomQueue[] {
  return custom.map((q) => {
    if (q.id !== id) return q;
    const { description: _d, jql: _j, ...rest } = q;
    const description = input.description?.trim();
    const jql = input.jql?.trim();
    return { ...rest, name: input.name.trim(), ...(description ? { description } : {}), ...(jql ? { jql } : {}) };
  });
}

export function removeCustomQueue(custom: CustomQueue[], id: string): CustomQueue[] {
  return custom.filter((q) => q.id !== id);
}

/** True when the edit changed something (the dialog's Save stays off otherwise). */
export function customQueueChanged(original: CustomQueue, input: CustomQueueInput): boolean {
  return original.name !== input.name.trim()
    || (original.description || '') !== (input.description || '').trim()
    || (original.jql || '') !== (input.jql || '').trim();
}
